/**
 * DocumentRedline — /documents/$id/redline (eDMS E3: redline accept/reject).
 *
 * Side-by-side review of the proposed changes against the current version. Each
 * change shows the removed text (strikethrough) and/or added text, with per-change
 * Accept / Reject controls and Accept-all / Reject-all. A summary tracks pending /
 * accepted / rejected so the reviewer knows when the review is complete.
 */
import { useParams, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button, StatCard, Stagger, Skeleton, EmptyState } from '@edms/design-system';
import {
  useRedlineQuery,
  useRedlineDecision,
  useRedlineBulk,
  type RedlineChange,
} from './useRedlineQuery';
import styles from './DocumentRedline.module.css';

const KIND_CLASS: Record<RedlineChange['kind'], string> = {
  ADDED: styles.kindAdded,
  REMOVED: styles.kindRemoved,
  MODIFIED: styles.kindModified,
};

export function DocumentRedline() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { t } = useTranslation();
  const q = useRedlineQuery(id);
  const decide = useRedlineDecision(id);
  const bulk = useRedlineBulk(id);

  const data = q.data;
  const busy = decide.isPending || bulk.isPending;

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <nav className={styles.crumbs} aria-label="Breadcrumb">
          <Link to="/documents/$id" params={{ id }} className={styles.crumb}>{t('redline.back', { defaultValue: 'Document' })}</Link>
          <span className={styles.crumbSep} aria-hidden>/</span>
          <span className={styles.crumbCurrent}>{t('redline.title', { defaultValue: 'Redline review' })}</span>
        </nav>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{t('redline.title', { defaultValue: 'Redline review' })}</h1>
          {data && data.items.length > 0 && (
            <div className={styles.actions}>
              <Button variant="secondary" size="sm" disabled={busy || data.summary.pending === 0}
                onClick={() => bulk.mutate({ decision: 'REJECTED' })}>
                {t('redline.rejectAll', { defaultValue: 'Reject all' })}
              </Button>
              <Button variant="primary" size="sm" disabled={busy || data.summary.pending === 0}
                onClick={() => bulk.mutate({ decision: 'ACCEPTED' })}>
                {t('redline.acceptAll', { defaultValue: 'Accept all' })}
              </Button>
            </div>
          )}
        </div>
      </header>

      {q.isLoading && (
        <>
          <div className={styles.kpiStrip} aria-hidden="true">{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={96} radius={14} />)}</div>
          {[0, 1, 2].map((i) => <Skeleton key={i} height={120} radius={12} />)}
        </>
      )}
      {q.isError && <p className={styles.error} role="alert">{t('redline.error', { defaultValue: 'Could not load redlines.' })}</p>}

      {data && (
        <>
          <Stagger className={styles.kpiStrip} step={50}>
            <StatCard label={t('redline.kpi.total', { defaultValue: 'Proposed changes' })} value={data.summary.total} accent="var(--spectro-purple, #9333EA)" />
            <StatCard label={t('redline.kpi.pending', { defaultValue: 'Pending' })} value={data.summary.pending}
              accent={data.summary.pending > 0 ? 'var(--color-status-warning, #D97706)' : 'var(--color-status-success, #16A34A)'}
              deltaCaption={data.summary.pending === 0 ? t('redline.kpi.done', { defaultValue: 'review complete' }) : undefined} />
            <StatCard label={t('redline.kpi.accepted', { defaultValue: 'Accepted' })} value={data.summary.accepted} accent="var(--color-status-success, #16A34A)" />
            <StatCard label={t('redline.kpi.rejected', { defaultValue: 'Rejected' })} value={data.summary.rejected} accent="var(--color-status-error, #DC2626)" />
          </Stagger>

          {data.items.length === 0 ? (
            <EmptyState icon="✓" title={t('redline.emptyTitle', { defaultValue: 'No proposed changes' })}
              description={t('redline.empty', { defaultValue: 'This version has no outstanding redlines to review.' })} />
          ) : (
            <ul className={styles.changeList}>
              {data.items.map((c) => (
                <li key={c.change_id} className={`${styles.change} ${c.status !== 'PENDING' ? styles.decided : ''}`}>
                  <div className={styles.changeHead}>
                    <span className={`${styles.kind} ${KIND_CLASS[c.kind]}`}>{c.kind}</span>
                    <span className={styles.section}>{c.section}</span>
                    <span className={styles.author}>{t('redline.proposedBy', { defaultValue: 'proposed by {{a}}', a: c.author })}</span>
                    <span className={styles.spacer} />
                    {c.status === 'PENDING' ? (
                      <span className={styles.decideBtns}>
                        <button type="button" className={`${styles.decideBtn} ${styles.reject}`} disabled={busy}
                          onClick={() => decide.mutate({ changeId: c.change_id, decision: 'REJECTED' })}>
                          {t('redline.reject', { defaultValue: 'Reject' })}
                        </button>
                        <button type="button" className={`${styles.decideBtn} ${styles.accept}`} disabled={busy}
                          onClick={() => decide.mutate({ changeId: c.change_id, decision: 'ACCEPTED' })}>
                          {t('redline.accept', { defaultValue: 'Accept' })}
                        </button>
                      </span>
                    ) : (
                      <span className={styles.decidedWrap}>
                        <span className={`${styles.statusPill} ${c.status === 'ACCEPTED' ? styles.accepted : styles.rejected}`}>
                          {c.status === 'ACCEPTED' ? t('redline.acceptedPill', { defaultValue: 'Accepted' }) : t('redline.rejectedPill', { defaultValue: 'Rejected' })}
                        </span>
                        <button type="button" className={styles.undo} disabled={busy}
                          onClick={() => decide.mutate({ changeId: c.change_id, decision: 'PENDING' })}>
                          {t('redline.undo', { defaultValue: 'Undo' })}
                        </button>
                      </span>
                    )}
                  </div>
                  <div className={styles.diff}>
                    {c.old_text && <p className={styles.removed}><span className={styles.diffMark}>−</span>{c.old_text}</p>}
                    {c.new_text && <p className={styles.added}><span className={styles.diffMark}>+</span>{c.new_text}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <footer className={styles.disclaimer}>
            {t('redline.disclaimer', { defaultValue: 'Accepted changes are merged into the next version on approval; rejected changes are dropped with the reviewer + decision recorded to the audit trail. Advisory until the version is approved + e-signed.' })}
          </footer>
        </>
      )}
    </main>
  );
}
